// © Microsoft Corporation. All rights reserved.

import { ActionHandler } from "@msnews/experiences-redux";
import { IReducer, IActionPayload } from "@msnews/experiences-redux-contracts";
import {
    AddTyper,
    DeleteActivities,
    DeleteUserComment,
    IncrementReplyCount,
    InsertComment,
    InsertComments,
    MapCmsIdToContentId,
    RecordSignalRHub,
    ResetError,
    ResetSpecificCommentId,
    SetContent,
    SetGradientOverlayData,
    SetPopup,
    SetPublisherSubscribeFollowButtonData,
    SetReactionBreakdown,
    SetTheme,
    SocialDataActions,
    UpdateActivities,
    UpdateActivityAppeal,
    UpdateActivityStatus,
    UpdateCommentRating,
    UpdateContent,
    UpdateContentRating,
    UpdateMe,
    UpdateNotifications,
    UpdateResponsiveClass,
    UpdateTypers,
    UpdateUser,
    UpdateUserComments,
    UpdateUserCommunitySettings,
    UpdateUserFollowers,
    UpdateUserFollowing
} from "./SocialData.actions";
import { Comment, Content, TypingUpdates, TypingUser, TypingUsers, User } from "@msnews/social-data-service";

import { SocialDataState } from "./SocialData.state";
import { contentIdKey } from "./utils/contentIdKey";
import { enums } from "./utils/enums";

/**
 * The reducer for the SocialData connector, and the SocialDataState
 */
export class SocialDataReducer implements IReducer<SocialDataState> {
    /**
   * Update the state for the <experience> connector, based on the action being applied
   * @param state The previous state
   * @param actionPayload The action being applied
   */
    public reduce(state: SocialDataState, actionPayload: IActionPayload): SocialDataState {
        if (!state) { return {}; }
        if (!actionPayload) { return state; }

        let newState: SocialDataState;

        ActionHandler.handleAction<SetTheme>(actionPayload, SocialDataActions.setTheme, (theme) => {
            theme = theme || state.theme || "default";
            newState = { ...state, theme };
        });

        ActionHandler.handleAction<UpdateMe>(actionPayload, SocialDataActions.updateMe, (me) => {
            newState = { ...state, me: me, users: updateUser(state.users, me.id, { data: me, isFetched: true, isFetching: false }), ...makeErrorIfUndefined("UpdateUser", { userId: me.id }) };
        });

        ActionHandler.handleAction<UpdateUserCommunitySettings>(actionPayload, SocialDataActions.updateUserCommunitySettings, (communitySettings) => {
            newState = { ...state, userCommunitySettings: communitySettings };
        });

        ActionHandler.handleAction<UpdateNotifications>(actionPayload, SocialDataActions.updateNotifications, (notifications) => {
            newState = { ...state, notifications: updateNotifications(state.notifications, notifications) };
        });

        ActionHandler.handleAction<UpdateActivities>(actionPayload, SocialDataActions.updateActivities, (activities) => {
            newState = { ...state, activities: updateActivities(state.activities, activities) };
        });

        ActionHandler.handleAction<DeleteActivities>(actionPayload, SocialDataActions.deleteActivities, () => {
            newState = { ...state, activities: { top: 0, skip: 0, hasMore: false, moreCount: 0, list: []} };
        });

        ActionHandler.handleAction<UpdateActivityStatus>(actionPayload, SocialDataActions.updateActivityStatus, (id, read) => {
            newState = { ...state, activities: updateActivityStatus(state.activities, id, read), notifications: updateNotificationStatus(state.notifications, id, read) };
        });

        ActionHandler.handleAction<UpdateActivityAppeal>(actionPayload, SocialDataActions.updateActivityAppeal, (id, appealSent) => {
            newState = { ...state, activities: updateActivityAppeal(state.activities, id, appealSent) };
        });

        ActionHandler.handleAction<SetContent>(actionPayload, SocialDataActions.setContent, (content, contentId) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: content }, ...makeErrorIfUndefined("SetContent", { contentId }) };
        });

        ActionHandler.handleAction<UpdateContent>(actionPayload, SocialDataActions.updateContent, (contentId, data) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: { ...state.contents[contentId], ...data } }, ...makeErrorIfUndefined("UpdateContent", { contentId }) };
        });

        ActionHandler.handleAction<UpdateContentRating>(actionPayload, SocialDataActions.updateContentRating, (contentId, rating) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: updateContentRating(state.contents[contentId], rating) }, ...makeErrorIfUndefined("UpdateContentRating", { contentId }) };
        });

        ActionHandler.handleAction<InsertComments>(actionPayload, SocialDataActions.insertComments, (contentId, comments, page, parentId, prepend?) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: insertComments(state.contents[contentId], comments, page, parentId, prepend) }, ...makeErrorIfUndefined("InsertComments", { contentId }) };
        });

        ActionHandler.handleAction<ResetSpecificCommentId>(actionPayload, SocialDataActions.resetSpecificCommentId, () => {
            newState = { ...state, specificCommentId: undefined };
        });

        ActionHandler.handleAction<UpdateCommentRating>(actionPayload, SocialDataActions.updateCommentRating, (contentId, commentId, rating, updateCounts) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: updateCommentRating(state.contents[contentId], commentId, rating, updateCounts)}, ...makeErrorIfUndefined("updateCommentRating", { contentId, commentId }) };
        });

        ActionHandler.handleAction<IncrementReplyCount>(actionPayload, SocialDataActions.incrementReplyCount, (contentId, commentId) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: incrementReplyCount(state.contents[contentId], commentId) }, ...makeErrorIfUndefined("IncrementReplyCount", { contentId, commentId }) };
        });

        ActionHandler.handleAction<InsertComment>(actionPayload, SocialDataActions.insertComment, (contentId, comment, parentId?, addedOffline?) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: insertComment(state.contents[contentId], comment, parentId, addedOffline) }, ...makeErrorIfUndefined("InsertComment", { contentId, comment }) };
        });

        ActionHandler.handleAction<UpdateUser>(actionPayload, SocialDataActions.updateUser, (userId, user) => {
            newState = { ...state, users: updateUser(state.users, userId, user), ...makeErrorIfUndefined("UpdateUser", { userId }) };
        });

        ActionHandler.handleAction<UpdateUserFollowers>(actionPayload, SocialDataActions.updateUserFollowers, (userId, followers) => {
            newState = { ...state, users: updateUserFollowers(state.users, userId, followers), ...makeErrorIfUndefined("UpdateUserFollowers", { userId }) };
        });

        ActionHandler.handleAction<UpdateUserFollowing>(actionPayload, SocialDataActions.updateUserFollowing, (userId, following) => {
            newState = { ...state, users: updateUserFollowing(state.users, userId, following), ...makeErrorIfUndefined("UpdateUserFollowing", { userId }) };
        });

        ActionHandler.handleAction<UpdateUserComments>(actionPayload, SocialDataActions.updateUserComments, (userId, comments, reset) => {
            newState = { ...state, users: updateUserComments(state.users, userId, comments, reset), ...makeErrorIfUndefined("UpdateUserComments", { userId, comments }) };
        });

        ActionHandler.handleAction<DeleteUserComment>(actionPayload, SocialDataActions.deleteUserComment, (userId, commentId, parentId) => {
            newState = { ...state, users: deleteUserComment(state.users, userId, commentId), contents: deleteComment(state.contents, commentId, parentId), ...makeErrorIfUndefined("DeleteUserComment", { userId, commentId }) };
        });

        ActionHandler.handleAction<SetReactionBreakdown>(actionPayload, SocialDataActions.setReactionBreakdown, (data) => {
            newState = { ...state, reactionBreakdown: data };
        });

        ActionHandler.handleAction<SetGradientOverlayData>(actionPayload, SocialDataActions.setGradientOverlayData, (data) => {
            newState = { ...state, gradientOverlayData: data };
        });

        ActionHandler.handleAction<SetPublisherSubscribeFollowButtonData>(actionPayload, SocialDataActions.setPublisherSubscribeFollowButtonData, (data) => {
            newState = { ...state, publisherSubscribeFollowButtonData: data };
        });
        ActionHandler.handleAction<SetPopup>(actionPayload, SocialDataActions.setPopup, (moduleName, props) => {
            newState = { ...state, popup: { moduleName, popupProps: props } };
        });

        ActionHandler.handleAction<AddTyper>(actionPayload, SocialDataActions.addTyper, (typer: TypingUser, contentId: string, id: string, market: string) => {
            newState = { ...state, contents: addTyper(id, market, state.contents, state.me, typer, contentId) };
        });

        ActionHandler.handleAction<UpdateTypers>(actionPayload, SocialDataActions.updateTypers, (expirationTime: number) => {
            newState = { ...state, contents: updateTypersIfNeeded(state.contents, state.me, expirationTime) };
        });

        ActionHandler.handleAction<RecordSignalRHub>(actionPayload, SocialDataActions.recordSignalRHub, (contentId: string) => {
            newState = { ...state, contents: { ...state.contents, [contentId]: recordSignalRHub(state.contents[contentId], contentId) } };
        });

        ActionHandler.handleAction<UpdateResponsiveClass>(actionPayload, SocialDataActions.updateResponsiveClass, (responsiveClass) => {
            newState = { ...state, responsiveClass: responsiveClass };
        });

        ActionHandler.handleAction<MapCmsIdToContentId>(actionPayload, SocialDataActions.mapCmsIdToContentId, (cmsId, market, contentId) => {
            newState = { ...state, contentIds: { ...state.contentIds, [contentIdKey(cmsId, market)]: contentId }, ...makeErrorIfUndefined("MapCmsIdToContentId", { contentId, cmsId }) };
        });

        ActionHandler.handleAction<ResetError>(actionPayload, SocialDataActions.resetError, () => {
            newState = { ...state, error: undefined };
        });

        return newState || state;
    }
}

function updateUser(users={}, userId, user) {
    const userSkeleton = {
        isFetched: false,
        isFetching: false,
        isFollowingFetched: false,
        isFollowingFetching: false,
        data: undefined
    };
    const oldUserState = users[userId] || userSkeleton;
    const newUsers = { ...users,
        [userId]: {
            ...oldUserState,
            ...user,
            data: {
                ...oldUserState.data,
                ...user.data
            }
        }
    };

    return newUsers;
}

function updateUserFollowers(users={}, userId, followers) {
    const oldUserState = users[userId];
    if (!oldUserState) { return users; }

    return {...users,
        [userId]: {...oldUserState,
            data: {...oldUserState.data,
                followers: {
                    ...oldUserState.data.followers,
                    ...followers,
                    list: oldUserState.data.followers.list.concat(followers.list)
                }
            }
        }
    };
}

function updateUserFollowing(users={}, userId, following) {
    const oldUserState = users[userId];
    if (!oldUserState) { return users; }

    return {...users,
        [userId]: {...oldUserState,
            data: {...oldUserState.data,
                following: {
                    ...oldUserState.data.following,
                    ...following,
                    list: oldUserState.data.following.list.concat(following.list)
                }
            }
        }
    };
}

function updateUserComments(users={}, userId, comments, reset=false) {
    const oldUserState = users[userId];
    if (!oldUserState) { return users; }

    return {...users,
        [userId]: {...oldUserState,
            data: {...oldUserState.data,
                comments: reset
                    ? comments
                    : {
                        ...oldUserState.data.comments,
                        ...comments,
                        list: oldUserState.data.comments.list.concat(comments.list)
                    }
            }
        }
    };
}

function deleteUserComment(users={}, userId, commentId) {
    const oldUserState = users[userId];
    if (!(oldUserState && oldUserState.data.comments)) { return users; }

    return {...users,
        [userId]: {...oldUserState,
            data: {...oldUserState.data,
                comments: {...oldUserState.data.comments,
                    list: markCommentAsDeleted(oldUserState.data.comments.list, commentId)
                }
            }
        }
    };
}

function insertComments(content, comments, page, parentId, prepend?) {
    const list = (content && content.comments && content.comments.list) ? content.comments.list : [];

    if (parentId) {
        return mapAllComments(content, comment =>
            comment.id == parentId ? {
                ...comment,
                replies: insertReplies(comment.replies, comments.list),
                replyCount: comments.totalCount,
                repliesHasPrevious: comments.hasPrevious !== undefined ? comments.hasPrevious : comment.repliesHasPrevious,
                repliesHasMore: comments.hasMore !== undefined ? comments.hasMore : comment.repliesHasMore
            } :
                comment
        );
    } else {
        let commentList: Array<Comment>;
        if (page) {
            commentList = prepend ? comments.list.concat(list) : list.concat(comments.list);
        } else {
            commentList = comments.list;
        }

        return {
            ...content,
            comments: {
                ...comments,
                list: commentList,
                hasPrevious: comments.hasPrevious !== undefined ? comments.hasPrevious : (content.comments && content.comments.hasPrevious),
                hasMore: comments.hasMore !== undefined ? comments.hasMore : (content.comments && content.comments.hasMore)
            }
        };
    }
}

function updateNotifications(existingNotifications, newNotifications) {
    const notificationList = existingNotifications ? existingNotifications.list : [];
    for (let index = 0; index < newNotifications.list.length; index++) {
        const newNotification = newNotifications.list[index];
        if (!notificationList.find(x => x.id == newNotification.id)) {
            notificationList.push(newNotification);
        }
    }
    notificationList.sort(function(a, b) {
        const keyA = new Date(a.datetime); const keyB = new Date(b.datetime);
        if (keyA < keyB) { return 1; }
        if (keyA > keyB) { return -1; }
        return 0;
    });
    return { ...newNotifications,
        list: notificationList.length > 3 ? notificationList.slice(0, 3) : notificationList
    };
}

function updateNotificationStatus(notifications, id, read) {
    if (!notifications) { return; }
    if (!read) { return notifications; }
    return { ...notifications,
        list: notifications.list.filter(x => x.id != id)
    };
}

function updateActivities(existingActivities, newActivities) {
    const activityList = existingActivities ? existingActivities.list : [];
    for (let index = 0; index < newActivities.list.length; index++) {
        const activity = newActivities.list[index];
        if (activity.type == enums.activityType.reaction) {
            const relatedActivity = activityList.find(x => x.type == enums.activityType.reaction && x.target.comment && x.target.comment.id == activity.target.comment.id);
            if (relatedActivity) {
                activity.skipRender = true;
            }
        }
        activityList.push(activity);
    }
    return { ...newActivities,
        list: activityList
    };
}

function updateActivityStatus(activities, id, read) {
    if (!activities) { return; }
    const activityList = activities ? activities.list : [];
    if (activityList.find(activity => activity.id == id)) {
        activityList.find(activity => activity.id == id).isSeen = read;
    }
    return { ...activities,
        list: activityList
    };
}

function updateActivityAppeal(activities, id, appealSent) {
    if (!activities) { return; }
    const activityList = activities ? activities.list : [];
    const index = activityList.findIndex(activity => activity.id == id);
    if (index >= 0) {
        activityList[index] = { ...activityList[index], appealSent: appealSent };
    }
    return { ...activities,
        list: activityList
    };
}

function updateCommentRating(content, commentId, rating, updateCounts) {
    return mapAllComments(content, $ => {
        if ($.id != commentId) { return $; }
        const comment = { ...$, rating: rating };
        if (updateCounts) {
            if ($.rating) {
                comment.reactionCounts[$.rating] = Math.max(0, comment.reactionCounts[$.rating] - 1);
            }
            if (rating) {
                comment.reactionCounts[rating] = Math.max(1, comment.reactionCounts[rating] + 1);
            }
        }
        return comment;
    });
}

function updateContentRating(content, rating) {
    content = content || { reactionCounts: {} };
    if (content.rating) {
        content.reactionCounts[content.rating] = Math.max(0, content.reactionCounts[content.rating] - 1);
    }
    if (rating) {
        content.reactionCounts[rating] = Math.max(1, content.reactionCounts[rating] + 1);
    }
    return {...content, rating: rating};
}

function mapAllComments(content: Content, mapFunc: (comment: Comment) => Comment): Content {
    function mapCommentAndReplies(comment: Comment): Comment {
        const newComment = mapFunc(comment);
        return {...newComment,
            replies: (newComment.replies ? newComment.replies.map(mapCommentAndReplies) : null)
        };
    }
    return {...content,
        comments: {...content.comments,
            list: (content.comments && content.comments.list)
                ? content.comments.list.map(mapCommentAndReplies)
                : null
        }
    };
}

function incrementReplyCount(content, commentId) {
    const newState = mapAllComments(content, $comment => {
        if ($comment.id !== commentId) { return $comment; }
        return {...$comment,
            replyCount: $comment.replyCount + 1
        };
    });
    if (newState.commentSummary) {
        newState.commentSummary.totalCount++;
        const replyCount = newState.commentSummary.subCommentSummaries.find(x => x.type == "Reply");
        if (replyCount) {
            replyCount.totalCount++;
        }
    }
    newState.totalCommentCount++;
    return newState;
}

function insertComment(content: Content, newComment, parentId?, addedOffline?: boolean): Content {
    newComment.addedOffline = Boolean(addedOffline);
    if (parentId) {
        const newState = mapAllComments(content, $comment => {
            if ($comment.id != parentId) { return $comment; }
            return {...$comment,
                replies: $comment.replies.concat(newComment)
            };
        });
        if (newState.commentSummary) {
            newState.commentSummary.totalCount++;
            const replyCount = newState.commentSummary.subCommentSummaries.find(x => x.type == "Reply");
            if (replyCount) {
                replyCount.totalCount++;
            }
        }
        newState.totalCommentCount++;
        return newState;
    } else {
        const newState = {
            ...content,
            comments: {...content.comments,
                list: [newComment].concat(content.comments.list)
            }
        };
        if (newState.commentSummary) {
            newState.commentSummary.totalCount++;
            const commentCount = newState.commentSummary.subCommentSummaries.find(x => x.type == "Comment");
            if (commentCount) {
                commentCount.totalCount++;
            }
        }
        newState.totalCommentCount++;
        return newState;
    }
}

function deleteComment(contents, commentId, parentId) {
    if (!contents) {
        return undefined;
    }

    function deleteCommentFromContent(content) {
        if (!content) {
            return undefined;
        }

        const list = (content && content.comments && content.comments.list) ? content.comments.list : [];

        if (parentId) {
            return mapAllComments(content, $comment => {
                if ($comment.id != parentId) { return $comment; }
                return {...$comment,
                    replies: markCommentAsDeleted($comment.replies, commentId)
                };
            });
        } else {
            return {...content,
                comments: {...content.comments,
                    list: markCommentAsDeleted(list, commentId)
                }
            };
        }
    }

    return mapAllContents(contents, deleteCommentFromContent);
}

function getDisplayedTypers(myId: string, typingUsers?: TypingUsers): TypingUser[] {
    const numDisplayed = 3; // configure to however many users you want to display
    if (!typingUsers) {
        return [];
    }
    const typingUserIds: Array<string> = Object.keys(typingUsers);
    if (typingUserIds.length === 0) {
        return [];
    }

    const displayedTypers: Array<TypingUser> = [];

    // If we are one of the typingUsers, we want ourselves to be at the front of the list.
    // this provides us with instant feedback from which we can infer that others are also
    // seeing that we're typing.
    if (myId && typingUsers[myId]) {
        displayedTypers.push(typingUsers[myId]);
    }

    for (const id of typingUserIds) {
        if (!myId || !typingUsers[myId] || typingUsers[myId].id !== id) {
            displayedTypers.push(typingUsers[id]);
        }
        if (displayedTypers.length >= numDisplayed) {
            return displayedTypers;
        }
    }

    return displayedTypers;
}

function mapAllContents(contents: { [id: string]: Content }, mapFunc: (content: Content) => Content): { [id: string]: Content } {
    const newContents: { [id: string]: Content } = {};
    for (const [contentId, content] of Object.entries(contents)) {
        newContents[contentId] = mapFunc(content);
    }
    return newContents;
}

function addTyper(currentId: string, currentMarket: string, contents: { [id: string]: Content }, me: User, typer: TypingUser, contentId: string): { [id: string]: Content } {
    return mapAllContents(contents, (content) => {
        const currentContentId = (content && content.id) || (currentId && currentMarket && currentId + "_" + currentMarket);
        if (currentContentId !== contentId || Object.keys(content.typingUpdates.typingUsers).includes(typer.id)) {
            return content;
        }
        const typingUpdates = { ...content.typingUpdates };
        const myId = me && me.id;
        const newTypingUsers: TypingUsers = { ...typingUpdates.typingUsers, [typer.id]: typer };
        typingUpdates.typingUsers = newTypingUsers;
        typingUpdates.displayedTypers = getDisplayedTypers(myId, newTypingUsers);
        typingUpdates.numTypers = Object.keys(newTypingUsers).length;
        return { ...content, typingUpdates };
    });
}

function updateTypersIfNeeded(contents: { [id: string]: Content }, me: User, expirationTime: number): { [id: string]: Content } {
    return mapAllContents(contents, (content) => {
        const typingUpdates: TypingUpdates = content && { ...content.typingUpdates };
        const myId: string = me && me.id;
        const cleaningStaleTypers = true; // set to false for testing
        if (typingUpdates && typingUpdates.typingUsers) {
            const newTypingUsers: TypingUsers = cleaningStaleTypers ? {} : { ...typingUpdates.typingUsers };
            for (const userId in typingUpdates.typingUsers) {
                const user = typingUpdates.typingUsers[userId];
                if (user.lastTypingTime >= expirationTime) {
                    newTypingUsers[userId] = user;
                }
            }
            return Object.keys(newTypingUsers).length == Object.keys(typingUpdates.typingUsers).length ? content : {
                ...content,
                typingUpdates: {
                    ...typingUpdates,
                    typingUsers: newTypingUsers,
                    displayedTypers: getDisplayedTypers(myId, newTypingUsers),
                    numTypers: Object.keys(newTypingUsers).length
                }
            };
        }
    });
}

function recordSignalRHub(content: Content, contentId: string) {
    const typingUpdates = {
        typingUsers: {},
        displayedTypers: [],
        numTypers: 0,
        hubName: contentId
    };
    return { ...content, typingUpdates };
}

function markCommentAsDeleted(comments, commentId) {
    const newComments = [];
    for (let index = 0; index < comments.length; index++) {
        const comment = comments[index];
        if (comment.id == commentId) {
            comment.isDeleted = true;
            comment.isHidden = true;
            comment.visibleStatus = "DeletedByUser";
        }
        newComments.push(comment);
    }
    return newComments;
}

function insertReplies(existingReplies, newReplies) {
    const replies = existingReplies.concat(newReplies).map(x => { x.addedOffline = false; return x; });
    const replyIds = replies.map(x => x.id);
    const distinctReplies = replies.filter(function(item, index) {
        return replyIds.indexOf(item.id) >= index;
    });
    distinctReplies.sort(function(a, b) {
        const keyA = new Date(a.createDatetime); const keyB = new Date(b.createDatetime);
        if (keyA < keyB) { return -1; }
        if (keyA > keyB) { return 1; }
        return 0;
    });
    return distinctReplies;
}

function makeErrorIfUndefined(action: string, args: Object): { error: { action: string, argument: string } } {
    for (const [key, value] of Object.entries(args)) {
        if (!value) {
            return { error: { action: action, argument: key } };
        }
    }
    return { error: undefined };
}
